import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '../../../components/layout/DashboardLayout';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Chip,
  FormControlLabel,
  Switch,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Check as CheckIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  VerifiedUser as VerifiedUserIcon,
  Send as SendIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { toast } from 'react-hot-toast';
import { getRoleDisplayName } from '../../../lib/utils';
import apiClient from '../../../lib/api';
import { useAuth } from '../../../context/AuthContext';

const UserDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sendingInvite, setSendingInvite] = useState(false);
  const [errors, setErrors] = useState({});
  const [user, setUser] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [branches, setBranches] = useState([]);
  const [selectedBranchId, setSelectedBranchId] = useState(''); // Currently selected branch in dropdown
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    isActive: true,
    companyId: '',
    branchIds: [],
  });

  useEffect(() => {
    if (id) {
      fetchUser();
    }
  }, [id]);

  useEffect(() => {
    const loadCompanies = async () => {
      try {
        if (currentUser?.type === 'admin') {
          const resp = await apiClient.getFuneralCompanies({ page: 1, limit: 100 });
          setCompanies(resp.data || []);
        }
      } catch (err) {
        console.error('Failed to load funeral companies', err);
      }
    };
    loadCompanies();
  }, [currentUser]);

  // Load branches when companyId changes
  useEffect(() => {
    const loadBranches = async () => {
      if (!formData.companyId) {
        setBranches([]);
        return;
      }
      try {
        const resp = await apiClient.getBranches({ companyId: formData.companyId, page: 1, limit: 100 });
        setBranches(resp.data || []);
      } catch (err) {
        console.error('Failed to load branches', err);
        setBranches([]);
      }
    };
    loadBranches();
  }, [formData.companyId]);

  const fetchUser = async () => {
    setLoading(true);
    try {
      const response = await apiClient.getUser(id);
      const userData = response.user || response.data || response;

      setUser(userData);
      setFormData({
        firstName: userData.firstName || '',
        lastName: userData.lastName || '',
        email: userData.email || '',
        phone: userData.phone || '',
        isActive: userData.isActive !== false,
        companyId: userData.companyId || '',
        branchIds: userData.branchIds || (userData.branchId ? [userData.branchId] : []), // Support both old single branchId and new branchIds array
        allBranches: userData.allBranches || false,
      });
    } catch (error) {
      console.error('Error fetching user:', error);
      toast.error('Error fetching user');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleBranchAdd = () => {
    if (!selectedBranchId) {
      toast.error('Please select a branch');
      return;
    }
    const currentBranchIds = formData.branchIds || [];
    if (currentBranchIds.includes(selectedBranchId)) {
      toast.error('This branch is already added');
      return;
    }
    handleInputChange('branchIds', [...currentBranchIds, selectedBranchId]);
    setSelectedBranchId(''); // Reset dropdown
  };

  const handleBranchRemove = (branchId) => {
    const currentBranchIds = formData.branchIds || [];
    handleInputChange('branchIds', currentBranchIds.filter(id => id !== branchId));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    if (currentUser?.type === 'admin' && !formData.companyId) {
      newErrors.companyId = 'Please select a funeral company';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setSaving(true);
    try {
      // Type field is not included in formData and shouldn't be changed
      await apiClient.updateUser(id, formData);
      toast.success('User updated successfully');
      // Refresh user data
      await fetchUser();
    } catch (error) {
      console.error('Error updating user:', error);
      const errorMessage = error.response?.data?.error || error.response?.data?.message || 'Error updating user';
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    navigate('/users');
  };

  const handleSendInvite = async () => {
    setSendingInvite(true);
    try {
      await apiClient.sendStaffInvite(id, {
        email: formData.email,
        firstName: formData.firstName,
        lastName: formData.lastName,
      });
      toast.success('Invite sent successfully');
    } catch (error) {
      console.error('Error sending invite:', error);
      const errorMessage = error.response?.data?.error || error.response?.data?.message || 'Error sending invite';
      toast.error(errorMessage);
    } finally {
      setSendingInvite(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout title="Loading User">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Loading user...</Typography>
        </Box>
      </DashboardLayout>
    );
  }

  if (!id || !user) {
    return (
      <DashboardLayout title="User Not Found">
        <Alert severity="error">User not found</Alert>
      </DashboardLayout>
    );
  }


  return (
    <DashboardLayout title="Edit User">
      <Box sx={{ maxWidth: 900, mx: 'auto' }}>
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box>
              <Typography variant="h4" component="h1">
                Edit User
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
                Update the user information below
              </Typography>
            </Box>
            <Button
              variant="contained"
              color="primary"
              startIcon={<SendIcon />}
              onClick={handleSendInvite}
              disabled={sendingInvite}
              sx={{ minWidth: 150 }}
            >
              {sendingInvite ? 'Sending...' : 'Send Invite'}
            </Button>
          </Box>
        </Box>

        {/* User Info Cards */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: 'repeat(2, 1fr)' },
            gap: 2 
          }}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <VerifiedUserIcon sx={{ mr: 1, fontSize: 20, color: 'text.secondary' }} />
                  <Typography variant="body2" color="text.secondary">
                    Email Status
                  </Typography>
                </Box>
                <Box sx={{ mt: 1 }}>
                  <Chip 
                    label={user.emailVerifiedAt ? 'Verified' : 'Unverified'}
                    color={user.emailVerifiedAt ? 'success' : 'warning'}
                    size="small"
                  />
                </Box>
              </CardContent>
            </Card>

            {user.roles && user.roles.length > 0 && (
              <Card sx={{ gridColumn: { xs: '1 / -1', sm: '1 / -1' } }}>
                <CardContent>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Roles
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                    {user.roles.map((userRole) => (
                      <Chip 
                        key={userRole.id}
                        label={getRoleDisplayName(userRole.role.name)}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </CardContent>
              </Card>
            )}
          </Box>
        </Box>

        {/* Edit Form */}
        <Card>
          <CardContent>
            <form onSubmit={handleSubmit}>
              <Box sx={{ 
                display: 'grid', 
                gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                gap: 3 
              }}>
                {/* Company selector visible to admins */}
                {currentUser?.type === 'admin' && (
                  <FormControl
                    fullWidth
                    required
                    error={!!errors.companyId}
                    sx={{ gridColumn: { xs: '1', sm: '1 / -1' } }}
                  >
                    <InputLabel>Funeral Company</InputLabel>
                    <Select
                      value={formData.companyId}
                      label="Funeral Company"
                      displayEmpty
                      onChange={(e) => {
                        handleInputChange('companyId', e.target.value);
                        // Clear branchIds when company changes
                        handleInputChange('branchIds', []);
                      }}
                    >
                      {/* <MenuItem value="" disabled>
                        Select a funeral company
                      </MenuItem> */}
                      {companies.length === 0 && (
                        <MenuItem value="" disabled>
                          No funeral companies found
                        </MenuItem>
                      )}
                      {companies.map((c) => (
                        <MenuItem key={c.id} value={c.id}>
                          {c.companyName || 'Unnamed Company'}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.companyId && (
                      <FormHelperText>{errors.companyId}</FormHelperText>
                    )}
                  </FormControl>
                )}


                <TextField
                  fullWidth
                  label="First Name"
                  value={formData.firstName}
                  onChange={(e) => handleInputChange('firstName', e.target.value)}
                  error={!!errors.firstName}
                  helperText={errors.firstName}
                  required
                />
                <TextField
                  fullWidth
                  label="Last Name"
                  value={formData.lastName}
                  onChange={(e) => handleInputChange('lastName', e.target.value)}
                  error={!!errors.lastName}
                  helperText={errors.lastName}
                  required
                />
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  error={!!errors.email}
                  helperText={errors.email}
                  required
                  sx={{ gridColumn: { xs: '1', sm: '1 / -1' } }}
                />
                <TextField
                  fullWidth
                  label="Phone"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  placeholder="Optional phone number"
                  sx={{ gridColumn: { xs: '1', sm: '1 / -1' } }}
                />
                <Box sx={{ gridColumn: { xs: '1', sm: '1 / -1' } }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.isActive}
                        onChange={(e) => handleInputChange('isActive', e.target.checked)}
                        color="primary"
                      />
                    }
                    label="Active User"
                  />
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5, ml: 4.5 }}>
                    Inactive users cannot log in to the system
                  </Typography>
                </Box>
              </Box>

              {/* Branches Section - Separate from form */}
              {(user?.type === 'staff' || formData.companyId) && (
                <Box sx={{ gridColumn: { xs: '1', sm: '1 / -1' }, mt: 2 }}>
                  <Divider sx={{ my: 3 }} />
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Branches
                  </Typography>

                  {/* All Branches Toggle */}
                  <Box sx={{ mb: 3, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1, backgroundColor: 'background.default' }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.allBranches || false}
                          onChange={(e) => {
                            const allBranches = e.target.checked;
                            handleInputChange('allBranches', allBranches);
                            // If enabling allBranches, clear branchIds
                            // If disabling, keep current branchIds
                            if (allBranches) {
                              handleInputChange('branchIds', []);
                            }
                          }}
                          color="primary"
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body1" fontWeight="medium">
                            Access to All Branches
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            When enabled, this user will have access to all branches for the company, including any new branches created in the future.
                          </Typography>
                        </Box>
                      }
                    />
                  </Box>
                  
                  {/* Branch selector and add button - only show if allBranches is false */}
                  {!formData.allBranches && (
                    <>
                      <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'flex-start' }}>
                    <FormControl
                      sx={{ minWidth: 250, flexGrow: 1 }}
                      disabled={!formData.companyId}
                    >
                      <InputLabel>Select Branch</InputLabel>
                      <Select
                        value={selectedBranchId}
                        label="Select Branch"
                        onChange={(e) => setSelectedBranchId(e.target.value)}
                        disabled={!formData.companyId}
                      >
                        <MenuItem value="">
                          <em>Select a branch...</em>
                        </MenuItem>
                        {branches.length === 0 && formData.companyId && (
                          <MenuItem value="" disabled>
                            No branches found for this company
                          </MenuItem>
                        )}
                        {branches
                          .filter(b => !formData.branchIds?.includes(b.id)) // Only show branches not already added
                          .map((b) => (
                            <MenuItem key={b.id} value={b.id}>
                              {b.branchName || 'Unnamed Branch'}
                            </MenuItem>
                          ))}
                      </Select>
                      {!formData.companyId && (
                        <FormHelperText>Select a company first to assign branches</FormHelperText>
                      )}
                    </FormControl>
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={handleBranchAdd}
                      disabled={!selectedBranchId || !formData.companyId}
                      sx={{ minWidth: 120 }}
                    >
                      Add
                      </Button>
                      </Box>

                      {/* Display selected branches in table */}
                      {formData.branchIds && formData.branchIds.length > 0 ? (
                    <TableContainer component={Paper} variant="outlined">
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>Branch Name</TableCell>
                            <TableCell align="right">Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {formData.branchIds.map((branchId) => {
                            const branch = branches.find(b => b.id === branchId);
                            if (!branch) return null;
                            return (
                              <TableRow key={branchId}>
                                <TableCell>
                                  <Typography variant="body2">
                                    {branch.branchName || 'Unnamed Branch'}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => handleBranchRemove(branchId)}
                                    aria-label="Remove branch"
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Box sx={{ 
                      p: 3, 
                      border: '1px dashed', 
                      borderColor: 'divider', 
                      borderRadius: 1,
                      textAlign: 'center',
                      backgroundColor: 'background.default',
                    }}>
                      <Typography variant="body2" color="text.secondary">
                        No branches assigned. Select a branch and click "Add" to assign branches.
                        </Typography>
                      </Box>
                    )}
                    </>
                  )}

                  {/* Show message when allBranches is enabled */}
                  {formData.allBranches && (
                    <Box sx={{ 
                      p: 3, 
                      border: '1px solid', 
                      borderColor: 'success.main', 
                      borderRadius: 1,
                      backgroundColor: 'success.50',
                    }}>
                      <Typography variant="body2" color="success.dark">
                        ✓ This user has access to all branches for the company ({branches.length} {branches.length === 1 ? 'branch' : 'branches'}). 
                        They will automatically receive access to any new branches created in the future.
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}

              {/* Action Buttons */}
              <Box sx={{ 
                display: 'flex', 
                gap: 2, 
                justifyContent: 'flex-end',
                mt: 4,
                pt: 3,
                borderTop: '1px solid',
                borderColor: 'divider'
              }}>
                <Button
                  variant="outlined"
                  onClick={handleBack}
                  startIcon={<ArrowBackIcon />}
                  sx={{ 
                    minWidth: 200
                  }}
                >
                  Back to Users
                </Button>
                
                <Button
                  type="submit"
                  variant="contained"
                  disabled={saving}
                  startIcon={saving ? null : <CheckIcon />}
                  sx={{ 
                    minWidth: 200
                  }}
                >
                  {saving ? 'Updating...' : 'Update User'}
                </Button>
              </Box>
            </form>
          </CardContent>
        </Card>
      </Box>
    </DashboardLayout>
  );
};

export default UserDetailPage;

